import React, { useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile, gcsDeleteFile, gcsGetSignedReadUrl, gcsCopyFile, gcsGetPublicUrl } from '@/lib/gcsStorage';
import { parseAiFlowResponse } from '@/lib/parseAiFlowResponse';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import {
  Upload,
  FileCheck,
  Loader2,
  AlertCircle,
  CheckCircle,
  User,
  Award,
  Calendar,
  Hash
} from 'lucide-react';

const CertificateUploadDialog = ({ open, onOpenChange, delegate, booking, onSuccess }) => {
  const { userProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [tempFilePath, setTempFilePath] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Editable certificate fields
  const [issueDate, setIssueDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [issueDateOpen, setIssueDateOpen] = useState(false);
  const [expiryDateOpen, setExpiryDateOpen] = useState(false);

  // Reset state when dialog closes
  const handleDialogChange = (isOpen) => {
    if (!isOpen) {
      setTempFilePath(null);
      setFileName(null);
      setUploading(false);
      setProcessing(false);
      setDragActive(false);
      setIssueDate(null);
      setExpiryDate(null);
      setCertificateNumber('');
      setIssueDateOpen(false);
      setExpiryDateOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    onOpenChange(isOpen);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      processFile(file);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file (JPG, PNG)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    await processFile(file);
  };

  const processFile = async (file) => {
    setUploading(true);
    const loadingToast = toast.loading('AI is analyzing certificate...');

    try {
      // 1. Upload to GCS temp folder
      const uploadResult = await gcsUploadFile(file, {
        folderPath: 'certificates/temp',
        fileName: `${Date.now()}_${file.name}`,
        contentType: file.type,
      });

      // 2. Get signed URL for doc parser
      const signedUrl = await gcsGetSignedReadUrl(uploadResult.filePath);

      setProcessing(true);

      // 3. Call doc parser webhook
      const response = await fetch('https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentUrl: signedUrl,
          fileName: file.name,
          uploadedAt: new Date().toISOString()
        })
      });

      if (!response.ok) throw new Error('Failed to process certificate');

      // 4. Extract data
      const responseData = await response.json();
      const parsed = parseAiFlowResponse(responseData);

      if (!parsed.success || !parsed.extractedData) {
        throw new Error('No data extracted from certificate');
      }

      const extractedInfo = parsed.extractedData;

      // Handle arrays OR single values
      const expiryDateStr = Array.isArray(extractedInfo.expiry_date)
        ? extractedInfo.expiry_date[0]
        : extractedInfo.expiry_date;
      const issueDateStr = Array.isArray(extractedInfo.issue_date)
        ? extractedInfo.issue_date[0]
        : extractedInfo.issue_date;
      const certNumber = Array.isArray(extractedInfo.certificate_number)
        ? extractedInfo.certificate_number[0]
        : extractedInfo.certificate_number;

      // Auto-populate fields with extracted data
      if (expiryDateStr) {
        const parsedDate = new Date(expiryDateStr);
        if (!isNaN(parsedDate.getTime())) {
          setExpiryDate(parsedDate);
        }
      }

      if (issueDateStr) {
        const parsedDate = new Date(issueDateStr);
        if (!isNaN(parsedDate.getTime())) {
          setIssueDate(parsedDate);
        }
      }

      if (certNumber) {
        setCertificateNumber(certNumber);
      }

      setTempFilePath(uploadResult.filePath);
      setFileName(file.name);

      toast.success('Certificate analyzed - please review details', { id: loadingToast });
    } catch (error) {
      console.error('Certificate upload error:', error);
      toast.error('Failed to process certificate', { id: loadingToast });
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!tempFilePath) {
      toast.error('Please upload a certificate file');
      return;
    }

    const loadingToast = toast.loading('Saving certificate...');
    setUploading(true);

    try {
      const profile = delegate.space_member?.user_profiles;
      const userId = delegate.space_member?.user_id;

      // 1. Copy file from temp to permanent location using server-side copy
      const permanentPath = `certificates/${userId}/${Date.now()}_${fileName}`;

      const { publicUrl } = await gcsCopyFile(tempFilePath, permanentPath);

      // Delete temp file
      await gcsDeleteFile(tempFilePath);

      const urlData = { publicUrl };

      // 2. Update booking_delegates with certificate info
      const { error: updateError } = await supabase
        .from('booking_delegates')
        .update({
          attendance_status: 'attended',
          completion_status: 'completed',
          certificate_url: urlData.publicUrl,
          certificate_issue_date: issueDate ? format(issueDate, 'yyyy-MM-dd') : null,
          certificate_expiry_date: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
          updated_date: new Date().toISOString(),
          updated_by: userProfile?.id
        })
        .eq('id', delegate.id);

      if (updateError) throw updateError;

      // 3. Update user_qualifications for cross-space sync
      if (booking.course_id && userId) {
        const { error: qualError } = await supabase
          .from('user_qualifications')
          .update({
            status: 'completed',
            renewal_status: 'completed',
            completed_date: issueDate ? format(issueDate, 'yyyy-MM-dd') : new Date().toISOString(),
            expiry_date: expiryDate ? format(expiryDate, 'yyyy-MM-dd') : null,
            due_date: null, // Clear due date since qualification is now complete
            evidence_url: urlData.publicUrl,
            certificate_number: certificateNumber || null,
            quote_id: booking.id, // Link to the booking that fulfilled this qualification
            notes: `Certificate uploaded for booking #${booking.id.split('-')[0]}`,
            updated_date: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('course_link_id', booking.course_id.toString());

        if (qualError) {
          console.warn('Failed to update user_qualifications:', qualError);
        }
      }

      toast.success('Certificate uploaded and synced across all spaces', { id: loadingToast });

      // Reset state and close dialog
      handleDialogChange(false);

      // Call success callback to refresh delegate list
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error('Error saving certificate:', error);
      toast.error('Failed to save certificate', { id: loadingToast });
    } finally {
      setUploading(false);
    }
  };

  const profile = delegate?.space_member?.user_profiles;

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Upload Training Certificate
          </DialogTitle>
          <DialogDescription>
            Upload a certificate for {profile?.display_name || 'this delegate'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Delegate Info */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{profile?.display_name}</p>
                <p className="text-xs text-gray-600">{profile?.email}</p>
              </div>
            </div>
            <Separator className="my-3" />
            <div className="flex items-center gap-2 text-sm">
              <Award className="h-4 w-4 text-blue-600" />
              <span className="font-medium">{booking?.course_name}</span>
            </div>
          </div>

          {/* AI Info Banner - Show before file upload */}
          {!tempFilePath && !processing && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <p className="font-medium mb-1">AI-Powered Certificate Processing</p>
                  <p>
                    Our AI will automatically extract certificate details. You can review and edit them before saving.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Dropzone */}
          <div
            className={`
              relative border-2 border-dashed rounded-xl p-8
              transition-all duration-200
              ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
              ${tempFilePath ? 'bg-green-50 border-green-300' : ''}
              ${processing ? 'opacity-60 pointer-events-none' : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              id="certificate-file"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              disabled={uploading || processing}
            />
            <label
              htmlFor="certificate-file"
              className="flex flex-col items-center cursor-pointer"
            >
              {processing ? (
                <>
                  <div className="relative mb-2">
                    <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">AI is extracting information...</p>
                  <p className="text-xs text-gray-500 mt-1">Reading certificate details</p>
                </>
              ) : tempFilePath ? (
                <>
                  <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
                  <p className="text-sm font-medium text-gray-900">{fileName}</p>
                  <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm font-medium text-gray-700">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG or PNG (max. 10MB)</p>
                </>
              )}
            </label>
          </div>

          {/* Certificate Details - Show after file upload */}
          {tempFilePath && !processing && (
            <>
              {/* Issue Date (Optional) */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Issue Date (Optional)
                </Label>
                <Popover open={issueDateOpen} onOpenChange={setIssueDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={uploading}
                    >
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {issueDate ? format(issueDate, 'PPP') : <span className="text-gray-500">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={issueDate}
                      onSelect={(date) => {
                        setIssueDate(date);
                        setIssueDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Expiry Date (Optional) */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Expiry Date <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Popover open={expiryDateOpen} onOpenChange={setExpiryDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      disabled={uploading}
                    >
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {expiryDate ? format(expiryDate, 'PPP') : <span className="text-gray-500">No expiry</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={expiryDate}
                      onSelect={(date) => {
                        setExpiryDate(date);
                        setExpiryDateOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Certificate Number (Optional) */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Certificate Number (Optional)
                </Label>
                <Input
                  type="text"
                  value={certificateNumber}
                  onChange={(e) => setCertificateNumber(e.target.value)}
                  placeholder="e.g., CERT-2024-001"
                  disabled={uploading}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleDialogChange(false)}
            disabled={uploading || processing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            disabled={!tempFilePath || uploading || processing}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Certificate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CertificateUploadDialog;
